import { useEffect, useState } from 'react';
import { fetchOverview } from '../services/statisticsService';
import type { StatisticsOverview } from '../services/types';

/** Zustand der geladenen Overview-Kennzahlen. */
export interface StatisticsOverviewState {
  /** Geladene Kennzahlen oder null (noch nicht geladen bzw. Fehler). */
  readonly data: StatisticsOverview | null;
  /** true, waehrend der Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null, wenn kein Fehler vorliegt. */
  readonly error: string | null;
}

/** Fehlermeldung fuer einen fehlgeschlagenen Overview-Request. */
const LOAD_ERROR_MESSAGE = 'Die Kennzahlen konnten nicht geladen werden.';

/**
 * Laedt die Overview-Kennzahlen (GET /api/statistics/overview) einmalig
 * beim Mount. Fehler werden still behandelt (nur setError, KEIN
 * console.error), damit die Kacheln den Fehlerzustand selbst rendern.
 */
export function useStatisticsOverview(): StatisticsOverviewState {
  const [data, setData] = useState<StatisticsOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchOverview(controller.signal)
      .then((overview) => {
        if (controller.signal.aborted) {
          // Abbruch durch Unmount: Ergebnis verwerfen (Symmetrie zum catch)
          return;
        }
        setData(overview);
        setLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) {
          // Abbruch durch Unmount: kein Fehlerzustand
          return;
        }
        setData(null);
        setError(LOAD_ERROR_MESSAGE);
        setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, []);

  return { data, loading, error };
}

export default useStatisticsOverview;
